import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { AsyncJobService } from '@recruitee/async-job/data';
import { HttpApiService } from '@recruitee/http';
import { BulkCandidateResponse } from '@recruitee/http/types';

export interface Ids {
  ids: number[];
}

const MAX_BULK_MOVE_CANDIDATES_COUNT = 20;

@Injectable({ providedIn: 'root' })
export class BulkActionsHttpClient {
  constructor(protected httpApi: HttpApiService, protected asyncJob: AsyncJobService) {}

  public addTask({ ids, task }: Ids & { task: any }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/task`, null, { candidates: ids, task: task });
  }

  public delete({ ids }: Ids): Observable<BulkCandidateResponse> {
    // update quick search history
    return this.httpApi.patch(`/bulk/candidates/destroy`, null, { candidates: ids });
  }

  public follow({ ids }: Ids): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/follow`, null, { candidates: ids });
  }

  public unfollow({ ids }: Ids): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/unfollow`, null, { candidates: ids });
  }

  public requalify({ ids, offerId }: Ids & { offerId: number }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/requalify`, null, {
      candidates: ids,
      offer_id: offerId,
    });
  }

  public disqualify({
    ids,
    offerId,
    disqualifyReasonId,
    runActions = null,
  }: Ids & {
    offerId: number;
    disqualifyReasonId: number;
    runActions?: boolean | null;
  }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(
      `/bulk/candidates/disqualify`,
      {
        ...(runActions !== null && { run_actions: runActions }),
      },
      {
        candidates: ids,
        offer_id: offerId,
        disqualify_reason_id: disqualifyReasonId,
      },
    );
  }

  public assign({
    ids,
    offersIds,
  }: Ids & { offersIds: number[] }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/assign`, null, {
      candidates: ids,
      offers_ids: offersIds,
    });
  }

  public unassign({
    ids,
    offersIds,
  }: Ids & { offersIds: number[] }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/unassign`, null, {
      candidates: ids,
      offers_ids: offersIds,
    });
  }

  public changeStage({
    ids,
    offerId,
    stageId,
    runActions = null,
    hiredAt,
    jobStartsAt,
  }: Ids & {
    offerId: number;
    stageId: number;
    runActions?: boolean | null;
    hiredAt?: string;
    jobStartsAt?: string;
  }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(
      `/bulk/candidates/change_stage`,
      {
        ...(runActions !== null && { run_actions: runActions }),
        hiredAt,
        jobStartsAt,
      },
      {
        candidates: ids,
        offer_id: offerId,
        stage_id: stageId,
        run_assistant: ids.length <= MAX_BULK_MOVE_CANDIDATES_COUNT,
      },
    );
  }

  public addTags({ ids, items }: Ids & { items: string[] }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/tags`, null, { candidates: ids, tags: items });
  }

  public addSources({ ids, items }: Ids & { items: string[] }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/sources`, null, {
      candidates: ids,
      sources: items,
    });
  }

  public extendGdprExpirationDate({
    ids,
    gdprCustomExpiresAt,
  }: Ids & { gdprCustomExpiresAt: string }): Observable<BulkCandidateResponse> {
    return this.httpApi.patch(`/bulk/candidates/set_gdpr_custom_expires_at`, null, {
      candidates: ids,
      gdpr_custom_expires_at: gdprCustomExpiresAt,
    });
  }

  public retrieve({ ids }: Ids): Observable<BulkCandidateResponse> {
    return this.httpApi.patch('/bulk/candidates/retrieve', { candidates: ids });
  }
}
